import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { createLocaleStore } from "./locale-store";
import type { LocalizedError } from "./localized-error";
import { describeError } from "./localized-error";

// Build target is explicit: a staging hostname must not silently enable
// unfinished catalogs in an otherwise production build.
export const localeStore = createLocaleStore({
  environment: import.meta.env.VITE_APP_ENV === "staging" ? "staging" :
    import.meta.env.DEV ? "development" : "production",
  languages: () => navigator.languages?.length ? navigator.languages : [navigator.language],
  surface: () => window.location.pathname.startsWith("/tg") ? "telegram" : "web",
  storage: () => localStorage,
  onLanguage: (locale) => { document.documentElement.lang = locale === "en-XA" ? "en" : locale; },
});

export function useLocale() {
  return useSyncExternalStore(localeStore.subscribe, localeStore.getSnapshot, localeStore.getSnapshot);
}

export function useAppTranslation() {
  return useTranslation(["common", "web", "errors"] as const, {
    i18n: localeStore.instance, useSuspense: false,
  });
}

/** Non-React render helpers read the current browser locale at call time.
 * Components using this helper must also subscribe with useAppTranslation. */
export const webText = localeStore.instance.t.bind(localeStore.instance);

// Keep transient dialog copy as a renderer when it is stored in React state.
// Raw identifiers/content remain unchanged; only Chatcode-owned text is rebuilt.
export type DisplayText = string | (() => string);
export function renderDisplayText(value: DisplayText): string {
  return typeof value === "function" ? value() : value;
}

export function localizedErrorText(error: LocalizedError | null): string {
  if (error?.key === "unknown" && error.context) return `${localeStore.instance.t(`errors:${error.context}`)}: ${localizedErrorText(describeError(error.detail))}`;
  return error?.key === "unknown" ? localeStore.instance.t("errors:unknown", { detail: error.detail }) :
    error ? localeStore.instance.t(`errors:${error.key}`) : "";
}

export function errorText(error: unknown): string {
  const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  if (raw === "Leaderboard is warming up.") return webText("web:copy_leaderboard_is_warming_up");
  if (raw === "Join the Chatcode channel to open the top chart.") return webText("web:copy_join_the_chatcode_channel_to_open_the_top_chart");
  if (raw === "Waiting for Telegram launch data…") return webText("web:copy_waiting_for_telegram_launch_data");
  const gatewayTimeout = /^Gateway update to (.+) timed out$/.exec(raw);
  if (gatewayTimeout) return webText("web:gateway_update_timeout", { version: gatewayTimeout[1] });
  const installTimeout = /^(.+) installation timed out\. Try again in a moment\.$/.exec(raw);
  if (installTimeout) return webText("web:agent_install_timeout", { agent: installTimeout[1] });
  const updateTimeout = /^(.+) update to (.+) timed out$/.exec(raw);
  if (updateTimeout) return webText("web:agent_update_timeout", { agent: updateTimeout[1], version: updateTimeout[2] });
  const agentFailure = /^Failed to update (.+?): ([\s\S]*)$/.exec(raw);
  if (agentFailure && agentFailure[1] !== "gateway") {
    const detail = describeError(agentFailure[2]);
    return webText("web:agent_update_failed", { agent: agentFailure[1], detail: detail.key === "unknown" && !detail.context ? detail.detail : localizedErrorText(detail) });
  }
  const startFailure = /^Failed to start (.+?) session: ([\s\S]*)$/.exec(raw);
  if (startFailure) {
    const detail = describeError(startFailure[2]);
    return webText("web:agent_start_failed", { agent: startFailure[1], detail: detail.key === "unknown" && !detail.context ? detail.detail : localizedErrorText(detail) });
  }
  const status = /^This session is ([a-z_]+) and cannot be reopened from Telegram\.$/.exec(raw);
  if (status) return webText("web:session_unavailable_status", { status: sessionStatusText(status[1]) });
  const currentStatus = /^Session is ([a-z_]+) right now\.$/.exec(raw);
  if (currentStatus) return webText("web:session_current_status", { status: sessionStatusText(currentStatus[1]) });
  const sizeLimit = /^File exceeds (\d+)MB limit\.$/.exec(raw);
  if (sizeLimit) return webText("web:transfer_size_limit", { size: sizeLimit[1] });
  return error ? localizedErrorText(describeError(error)) : "";
}

export function sessionStatusText(status: string): string {
  switch (status) {
    case "starting": return webText("web:session_status_starting");
    case "running": return webText("web:session_status_running");
    case "ended": return webText("web:session_status_ended");
    case "closed": return webText("web:session_status_closed");
    case "failed": return webText("web:session_status_failed");
    case "ending": return webText("web:session_status_ending");
    default: return status;
  }
}

export function startLocaleSync(): () => void {
  const storage = (event: StorageEvent) => { void localeStore.onStorage(event).catch(() => {}); };
  const languages = () => { void localeStore.refreshDetectedLanguage().catch(() => {}); };
  window.addEventListener("storage", storage);
  window.addEventListener("languagechange", languages);
  void localeStore.initialize().catch(() => {}); // English remains available if preload fails.
  return () => {
    window.removeEventListener("storage", storage);
    window.removeEventListener("languagechange", languages);
  };
}
